import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Clapperboard, CornerDownLeft, Film, HardDrive, LayoutGrid, Plus, ScrollText, Search, Settings, Users } from 'lucide-react';
import { api, thumbUrl } from '../lib/api';
import { useMediaList } from '../lib/live';
import { useSession } from '../lib/session';
import { shouldAnimate } from '../lib/format';
import { watchTogether } from '../features/room/session';
import { gsap } from '../motion/gsap';
import { useLightDismiss } from '../ui/lightDismiss';
import type { RoomRecord } from '../lib/types';

interface Entry {
  id: string;
  group: string;
  label: string;
  hint?: string;
  icon: React.ReactNode;
  thumb?: string;
  run: () => void;
}

/** Cmd/Ctrl+K palette: jump to a page, a room or a title, or start a room. */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const nav = useNavigate();
  const host = useSession((s) => s.user?.host);
  const media = useMediaList((s) => s.items);
  const refreshMedia = useMediaList((s) => s.refresh);
  const [rooms, setRooms] = useState<RoomRecord[]>([]);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const list = useRef<HTMLDivElement>(null);

  // Opening starts from a clean search. Reset while rendering so the first open frame is already clean.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setQ('');
      setActive(0);
    }
  }
  useEffect(() => {
    if (!open) return;
    refreshMedia();
    api.rooms().then(setRooms).catch(() => undefined);
  }, [open, refreshMedia]);

  // A native modal <dialog> (focus stays inside, Escape is its cancel event); it closes once the
  // exit tween ends.
  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (open) {
      if (!d.open) d.showModal();
      input.current?.focus();
      if (!shouldAnimate()) return;
      gsap.fromTo(d, { opacity: 0 }, { opacity: 1, duration: 0.2, ease: 'none' });
      gsap.fromTo(panel.current, { y: -18, scale: 0.96, opacity: 0 }, { y: 0, scale: 1, opacity: 1, duration: 0.35, ease: 'power3.out' });
    } else if (d.open) {
      if (!shouldAnimate()) return d.close();
      gsap.to(panel.current, { y: -10, scale: 0.98, opacity: 0, duration: 0.16, ease: 'power2.in' });
      gsap.to(d, { opacity: 0, duration: 0.18, ease: 'none', onComplete: () => d.close() });
    }
  }, [open]);
  useLightDismiss(dialog, open);

  const go = (path: string) => () => {
    onClose();
    nav(path);
  };

  const entries = useMemo<Entry[]>(() => {
    const pages: Entry[] = [
      { id: 'p-overview', group: 'Go to', label: 'Overview', icon: <LayoutGrid size={15} />, run: go('/') },
      { id: 'p-rooms', group: 'Go to', label: 'Rooms', icon: <Clapperboard size={15} />, run: go('/rooms') },
      { id: 'p-drive', group: 'Go to', label: 'Drive', icon: <HardDrive size={15} />, run: go('/drive') },
      { id: 'p-team', group: 'Go to', label: 'Team', icon: <Users size={15} />, run: go('/team') },
      { id: 'p-settings', group: 'Go to', label: 'Settings', icon: <Settings size={15} />, run: go('/settings') },
      ...(host ? [{ id: 'p-logs', group: 'Go to', label: 'Logs', icon: <ScrollText size={15} />, run: go('/logs') }] : []),
      { id: 'a-new', group: 'Actions', label: 'New room', icon: <Plus size={15} />, run: go('/rooms?new=1') },
    ];
    const roomEntries: Entry[] = rooms.map((r) => ({
      id: `r-${r.id}`,
      group: 'Rooms',
      label: r.name,
      hint: r.media?.title,
      icon: <Clapperboard size={15} />,
      run: go(`/room/${r.id}`),
    }));
    const mediaEntries: Entry[] = media.map((m) => ({
      id: `m-${m.id}`,
      group: 'Titles',
      label: m.title,
      hint: m.playable ? 'Watch together' : 'Open in Drive',
      icon: <Film size={15} />,
      thumb: thumbUrl(m),
      run: () => {
        onClose();
        if (m.playable) watchTogether(m, nav);
        else nav(`/drive?q=${encodeURIComponent(m.title)}`);
      },
    }));
    const all = [...pages, ...roomEntries, ...mediaEntries];
    const needle = q.trim().toLowerCase();
    if (!needle) return [...pages, ...roomEntries.slice(0, 4), ...mediaEntries.slice(0, 5)];
    return all.filter((e) => `${e.label} ${e.hint ?? ''}`.toLowerCase().includes(needle)).slice(0, 30);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, rooms, media, host]);

  const searchEmpty = q === '';
  // The result list tweens to its new height instead of jumping when the matches change.
  const lastHeight = useRef(0);
  useLayoutEffect(() => {
    const el = list.current;
    if (!el) return;
    const next = el.offsetHeight;
    if (lastHeight.current && lastHeight.current !== next && shouldAnimate())
      gsap.fromTo(el, { height: lastHeight.current }, { height: next, duration: 0.24, ease: 'power3.out', clearProps: 'height', overwrite: true });
    lastHeight.current = next;
  }, [entries.length, open]);
  useEffect(() => {
    if (!open || !shouldAnimate() || !list.current) return;
    gsap.fromTo(list.current.querySelectorAll('.cmdk-item'), { y: 8, opacity: 0 }, { y: 0, opacity: 1, duration: 0.25, stagger: 0.015, ease: 'power2.out', clearProps: 'transform,opacity' });
  }, [open, searchEmpty]);
  useEffect(() => {
    list.current?.querySelector('.cmdk-item.on')?.scrollIntoView({ block: 'nearest' });
  }, [active]);


  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, entries.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      entries[active]?.run();
    }
  };

  let lastGroup = '';
  return createPortal(
    <dialog
      ref={dialog}
      className="cmdk-dialog"
      aria-label="Search"
      onCancel={(e) => {
        e.preventDefault(); // close after the exit tween
        onClose();
      }}
      // Clicking outside closes it too (light dismiss); keyboard users use Escape.
      closedby="any"
    >
      <div className="cmdk" ref={panel}>
        <div className="cmdk-input">
          <Search size={16} />
          <input
            ref={input}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKey}
            placeholder="Search titles, rooms and pages"
            aria-label="Search"
          />
          <span className="kbd">esc</span>
        </div>
        <div className="cmdk-list" ref={list}>
          {entries.length === 0 && <div className="cmdk-empty">Nothing matches "{q}"</div>}
          {entries.map((e, i) => {
            const head = e.group !== lastGroup ? <div className="cmdk-group">{e.group}</div> : null;
            lastGroup = e.group;
            return (
              <div key={e.id}>
                {head}
                <button className={`cmdk-item${i === active ? ' on' : ''}`} onMouseMove={() => setActive(i)} onClick={e.run}>
                  {e.thumb ? <span className="cmdk-thumb" style={{ backgroundImage: `url(${e.thumb})` }} /> : <span className="cmdk-icon">{e.icon}</span>}
                  <span className="cmdk-label">{e.label}</span>
                  {e.hint && <span className="cmdk-hint">{e.hint}</span>}
                  {i === active && <CornerDownLeft size={13} className="cmdk-enter" />}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </dialog>,
    document.body,
  );
}
